import { useCallback, useEffect, useRef, useState } from 'react'
import { useParams, Link as RouterLink } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  Link as MuiLink,
  Skeleton,
  Typography,
} from '@mui/material'
import { NavigateNext as NavigateNextIcon, Home as HomeIcon } from '@mui/icons-material'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { ErrorPage } from '@/components/common/ErrorPage'
import { PageHelpPopover } from '@/components/common/PageHelpPopover'
import { ScheduleReadonlyPreview } from '@/components/lessonSchedule/ScheduleReadonlyPreview'
import { UserLessonScheduleEditModal } from '@/components/lessonSchedule/UserLessonScheduleEditModal'
import { lessonSchedulesApi } from '@/api/lessonSchedules'
import { useUserStore } from '@/stores/userStore'
import { ROUTES } from '@/utils/constants'
import { getUserDisplayName } from '@/utils/userName'
import { displayErrorMessage, getErrorMessage } from '@/utils/error'
import type { LessonScheduleTemplate } from '@/types'
import '@/styles/dashboard.css'

/**
 * Расписание ученика в его карточке (учитель): повторяющиеся слоты и паузы,
 * по которым создаются автоматические уроки. Ученику в группе показывается
 * ещё и расписание группы — только просмотр, правится оно на странице группы.
 */
export function UserSchedulePage() {
  const { id } = useParams<{ id: string }>()
  const { t } = useTranslation()
  const userId = id ? parseInt(id, 10) : NaN
  const { currentUser, isLoading, error, errorStatus, fetchUser } = useUserStore()
  // Для какого ученика загрузка завершилась. Не булев флаг: при переходе
  // «назад» между двумя учениками компонент переиспользуется, и флаг прошлой
  // загрузки на кадр показывал страницу ошибки вместо скелетона
  const [loadedUserId, setLoadedUserId] = useState<number | null>(null)

  const [userSchedule, setUserSchedule] = useState<LessonScheduleTemplate | null>(null)
  const [groupSchedule, setGroupSchedule] = useState<LessonScheduleTemplate | null>(null)
  const [scheduleLoading, setScheduleLoading] = useState(true)
  const [scheduleError, setScheduleError] = useState<string | null>(null)
  const [scheduleEditOpen, setScheduleEditOpen] = useState(false)
  // Номер последней загрузки: ответ устаревшего запроса (сменился ученик,
  // перезагрузка после сохранения) не должен перетереть свежие данные.
  const scheduleRequestRef = useRef(0)

  // В сторе может ещё лежать ученик с предыдущей страницы — пока не пришёл
  // нужный, расписание не грузим, иначе подтянется чужая группа.
  const student = currentUser?.id === userId ? currentUser : null
  const studentId = student?.id ?? null
  const groupId = student?.contract?.group_id ?? null

  useEffect(() => {
    if (Number.isNaN(userId)) return
    let cancelled = false
    void fetchUser(userId).finally(() => {
      if (!cancelled) setLoadedUserId(userId)
    })
    return () => {
      cancelled = true
    }
  }, [userId, fetchUser])

  const loadSchedulePreview = useCallback(async () => {
    if (studentId === null) return
    const requestId = ++scheduleRequestRef.current
    setScheduleLoading(true)
    setScheduleError(null)
    try {
      const [own, group] = await Promise.all([
        lessonSchedulesApi.getUserSchedule(studentId),
        groupId ? lessonSchedulesApi.getGroupSchedule(groupId) : Promise.resolve(null),
      ])
      if (requestId !== scheduleRequestRef.current) return
      setUserSchedule(own)
      setGroupSchedule(group)
    } catch (e: unknown) {
      if (requestId !== scheduleRequestRef.current) return
      setScheduleError(displayErrorMessage(getErrorMessage(e), t('lessonSchedules.loadError')))
      setUserSchedule(null)
      setGroupSchedule(null)
    } finally {
      if (requestId === scheduleRequestRef.current) setScheduleLoading(false)
    }
  }, [studentId, groupId, t])

  useEffect(() => {
    void loadSchedulePreview()
  }, [loadSchedulePreview])

  if (Number.isNaN(userId)) {
    return <ErrorPage fullPage statusCode={404} />
  }

  if (!student) {
    if (loadedUserId !== userId || isLoading) {
      return (
        <DashboardLayout>
          <Box sx={{ p: 3 }}>
            <Skeleton variant="text" width={300} height={32} sx={{ mb: 2 }} />
            <Skeleton variant="text" width={200} height={48} sx={{ mb: 1 }} />
            <Skeleton variant="text" width="60%" height={24} sx={{ mb: 3 }} />
            <Skeleton variant="rounded" width={220} height={40} sx={{ mb: 2 }} />
            <Card variant="outlined">
              <CardContent>
                <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 1 }} />
              </CardContent>
            </Card>
          </Box>
        </DashboardLayout>
      )
    }
    return <ErrorPage fullPage statusCode={errorStatus ?? 500} message={error ?? undefined} />
  }

  const isInGroup = groupId !== null
  const studentDisplayName = getUserDisplayName(student, { prefer: 'full_name' })

  return (
    <DashboardLayout>
      <Box sx={{ p: 3 }}>
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          aria-label="breadcrumb"
          sx={{ mb: 2 }}
        >
          <MuiLink
            component={RouterLink}
            to={ROUTES.DASHBOARD}
            underline="hover"
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            {t('dashboard.home')}
          </MuiLink>
          <MuiLink component={RouterLink} to={ROUTES.USERS} underline="hover">
            {t('dashboard.students')}
          </MuiLink>
          <MuiLink component={RouterLink} to={`${ROUTES.USERS}/${student.id}`} underline="hover">
            {studentDisplayName || t('profile.title')}
          </MuiLink>
          <Typography color="text.primary">{t('lessonSchedules.title')}</Typography>
        </Breadcrumbs>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
          <Typography variant="h4" component="h1" fontWeight={700} sx={{ flexGrow: 1 }}>
            {t('lessonSchedules.title')}
          </Typography>
          <PageHelpPopover pageKey="user-schedule" />
        </Box>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          {t('lessonSchedules.subtitle')}
        </Typography>

        {scheduleError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setScheduleError(null)}>
            {scheduleError}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-end', gap: 1, mb: 2 }}>
          <Button
            variant="contained"
            size="small"
            onClick={() => setScheduleEditOpen(true)}
            sx={{ height: 40, width: { xs: '100%', sm: 'auto' } }}
          >
            {t('lessonSchedules.userEdit')}
          </Button>
        </Box>

        {isInGroup && (
          <Card variant="outlined" sx={{ mb: 2, bgcolor: 'var(--color-disable-surface)' }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                {t('lessonSchedules.groupTitle')}
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {t('lessonSchedules.userGroupHint')}
                </Typography>
                <MuiLink
                  component={RouterLink}
                  to={`${ROUTES.GROUPS}/${groupId}`}
                  underline="hover"
                  fontWeight={600}
                >
                  {student.contract?.group?.name
                    ? t('lessonSchedules.userOpenGroup', { name: student.contract.group.name })
                    : t('lessonSchedules.userOpenGroupShort')}
                </MuiLink>
              </Box>
              <ScheduleReadonlyPreview schedule={groupSchedule} loading={scheduleLoading} />
            </CardContent>
          </Card>
        )}

        <Card variant="outlined">
          <CardContent>
            {isInGroup && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                  {t('lessonSchedules.userIndividualTitle')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('lessonSchedules.userIndividualHint')}
                </Typography>
              </Box>
            )}
            <ScheduleReadonlyPreview schedule={userSchedule} loading={scheduleLoading} />
          </CardContent>
        </Card>

        <UserLessonScheduleEditModal
          open={scheduleEditOpen}
          onClose={() => setScheduleEditOpen(false)}
          userId={student.id}
          schedule={userSchedule}
          onSaved={loadSchedulePreview}
        />
      </Box>
    </DashboardLayout>
  )
}
